import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useShop } from '../context/ShopContext';

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const location = useLocation();
    const { shopId, setShopId } = useShop();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlShopId = searchParams.get('shop_id');
        const currentShopId = urlShopId || shopId;

        if (urlShopId && String(urlShopId) !== String(shopId)) {
            setShopId(String(urlShopId));
        }

        if (!currentShopId) {
            window.location.href = '/';
            return;
        }

        let url = id ? `/api/products?id=${id}` : '/api/products';
        url += `${url.includes('?') ? '&' : '?'}shop_id=${currentShopId}`;

        setLoading(true);

        axios.get(url, { withCredentials: true })
            .then(res => {
                const list = Array.isArray(res.data) ? res.data : [];
                setProducts(list);
            })
            .catch(err => {
                console.error('상품 목록 조회 실패:', err);
                setProducts([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, location.search, shopId, setShopId]);

    const handleWishToggle = async (e, productId) => {
        e.preventDefault();
        e.stopPropagation();

        const targetProduct = products.find(p => p.id === productId);
        const wasWished = !!targetProduct?.is_wished;

        // 낙관적 UI
        setProducts(prev =>
            prev.map(p =>
                p.id === productId
                    ? { ...p, is_wished: !wasWished }
                    : p
            )
        );

        try {
            const res = await axios.post(
                '/api/product/wishlist/toggle',
                new URLSearchParams({ product_id: productId }),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    withCredentials: true
                }
            );

            const data = res.data || {};

            if (!data.success) {
                setProducts(prev =>
                    prev.map(p =>
                        p.id === productId
                            ? { ...p, is_wished: wasWished }
                            : p
                    )
                );

                if (data.need_login) {
                    alert('로그인이 필요합니다.');
                    window.location.href = '/login';
                    return;
                }

                alert(data.message || '처리 실패');
            }
        } catch (err) {
            console.error('찜 처리 실패:', err);

            setProducts(prev =>
                prev.map(p =>
                    p.id === productId
                        ? { ...p, is_wished: wasWished }
                        : p
                )
            );

            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                alert('로그인이 필요합니다.');
                window.location.href = '/login';
                return;
            }

            alert('통신 오류');
        }
    };

    return (
        <div className="product-list-wrapper">
            {loading ? (
                <div className="empty-box">
                    상품을 불러오는 중입니다.
                </div>
            ) : products.length === 0 ? (
                <div className="empty-box">
                    상품이 없습니다.
                </div>
            ) : (
                <div className="product-grid">
                    {products.map(p => (
                        <div className="product-item" key={p.id}>
                            <div className="product-card-wrap">
                                <button
                                    type="button"
                                    className={`btn-wish-list ${p.is_wished ? 'active' : ''}`}
                                    onClick={(e) => handleWishToggle(e, p.id)}
                                >
                                    <span className="wish-icon">
                                        {p.is_wished ? '♥' : '♡'}
                                    </span>
                                </button>

                                <Link to={`/product/${p.id}`} className="product-link">
                                    <div className="card-custom">
                                        <div className="card-img-box">
                                            <img
                                                src={p.image_url || '/api/static/no-image.png'}
                                                alt={p.name}
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/600x600?text=No+Image';
                                                }}
                                            />
                                        </div>

                                        <div className="card-body d-none d-md-flex">
                                            <h5 className="card-title">{p.name}</h5>

                                            {p.short_description ? (
                                                <p className="card-text">
                                                    {p.short_description}
                                                </p>
                                            ) : null}

                                            <div className="card-footer-price">
                                                <strong>
                                                    {
                                                    p.price < p.original_price ? (
                                                        <>
                                                        <del style={{ fontSize: '0.7rem', color: '#999' }}>{Number(p.original_price).toLocaleString()}원</del>
                                                        <div>{Number(p.price).toLocaleString()}원</div>
                                                        </>
                                                    ) : (
                                                        <>{Number(p.price).toLocaleString()}원</>
                                                    )
                                                    }
                                                </strong>
                                            </div>
                                        </div>

                                        <div className="product-info-mobile">
                                            <div className="product-name-mobile">{p.name}</div>
                                            <div className="price-mobile">
                                                {
                                                p.price < p.original_price ? (
                                                    <>
                                                    <del style={{ fontSize: '0.7rem', color: '#999' }}>{Number(p.original_price).toLocaleString()}원</del>
                                                    <div>{Number(p.price).toLocaleString()}원</div>
                                                    </>
                                                ) : (
                                                    <>{Number(p.price).toLocaleString()}원</>
                                                )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <style>{`
                .product-list-wrapper {
                    max-width: 1200px;
                    margin: 20px auto 0;
                    padding: 0 15px;
                }

                .product-grid {
                    display: flex;
                    flex-wrap: wrap;
                    margin: 0 -8px;
                }

                .product-item {
                    width: 50%;
                    padding: 8px;
                    box-sizing: border-box;
                }

                .product-card-wrap {
                    position: relative;
                    height: 100%;
                }

                .product-link {
                    text-decoration: none !important;
                    color: inherit !important;
                    display: block;
                    height: 100%;
                }

                .product-link:hover,
                .product-link:focus,
                .product-link:active {
                    text-decoration: none !important;
                    color: inherit !important;
                    outline: none;
                }

                .card-custom {
                    background: #fff;
                    border-radius: 0;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    border: none;
                    box-shadow: none;
                    transition: opacity 0.2s ease-in-out;
                    position: relative;
                }

                .product-link:hover .card-custom {
                    opacity: 0.8;
                }

                .card-img-box {
                    width: 100%;
                    background-color: #fff;
                }

                .card-img-box img {
                    width: 100%;
                    aspect-ratio: 1 / 1;
                    object-fit: cover;
                    display: block;
                }

                .card-body {
                    padding: 1rem;
                    display: none;
                    flex-direction: column;
                    flex: 1 1 auto;
                }

                .card-title {
                    font-size: 0.95rem;
                    font-weight: 700;
                    margin-bottom: 4px;
                    color: #222;
                    text-decoration: none !important;
                }

                .card-text {
                    font-size: 0.9rem;
                    color: #6c757d;
                    margin-bottom: 1rem;
                    line-height: 1.4;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .card-footer-price {
                    margin-top: auto;
                    font-size: 0.95rem;
                    color: #666;
                    font-weight: 700;
                }

                .product-info-mobile {
                    flex: 1 1 50%;
                    max-width: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    padding: 6px 0;
                }

                .product-name-mobile {
                    font-weight: 700;
                    font-size: 0.95rem;
                    margin-bottom: 4px;
                    color: #222;
                    line-height: 1.2;
                }

                .price-mobile {
                    font-weight: 700;
                    font-size: 0.95rem;
                    color: #666;
                    line-height: 1.2;
                }

                .btn-wish-list {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    z-index: 10;
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    border: none;
                    background: rgba(255, 255, 255, 0.85);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    cursor: pointer;
                }

                .wish-icon {
                    font-size: 16px;
                    line-height: 1;
                    color: #aaa;
                    transition: color 0.15s ease;
                }

                .btn-wish-list.active .wish-icon {
                    color: red;
                }

                .btn-wish-list:hover {
                    background: rgba(255, 255, 255, 0.95);
                }

                .empty-box {
                    text-align: center;
                    color: #666;
                    padding: 60px 0;
                    font-size: 1rem;
                }

                @media (max-width: 767.98px) {
                    .product-info-mobile {
                        justify-content: flex-start !important;
                        gap: 4px;
                        padding: 6px 0;
                    }

                    .product-name-mobile,
                    .price-mobile {
                        margin: 0;
                        line-height: 1.2;
                    }
                }

                @media (min-width: 768px) {
                    .product-item {
                        width: 33.333%;
                    }

                    .card-custom {
                        box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                    }

                    .card-body {
                        display: flex;
                    }

                    .product-info-mobile {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default ProductList;